import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { buildRedirectUri } from '@/components/brand-connections/utils/oauth';
import {
  finishOAuthPopup,
  functionErrorMessage,
  leaveCallback,
  popupMarkerFor,
  type PopupMarker,
} from '@/components/brand-connections/utils/connect';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type GAProperty = {
  accountName?: string | null;
  property_id: string;
  property_name?: string | null;
};

export default function GoogleCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const didRun = useRef(false);

  const [brandId, setBrandId] = useState<string | null>(null);
  const [redirectUri, setRedirectUri] = useState<string>('');

  const [loading, setLoading] = useState(true);
  const [needsSelection, setNeedsSelection] = useState(false);
  const [properties, setProperties] = useState<GAProperty[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [savingSelection, setSavingSelection] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, GAProperty[]>();
    for (const p of properties) {
      const k = (p.accountName ?? 'Google Account').toString();
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    }
    return Array.from(map.entries());
  }, [properties]);

  // Set when this runs in a popup a page opened (the Analytics or Connections
  // page's connect dialog): it reports back and closes instead of navigating.
  const marker = useRef<PopupMarker | null>(null);
  const [popupDone, setPopupDone] = useState<string | null>(null);
  // The brand this sign-in is for, once read from the state.
  const brandRef = useRef<string | null>(null);

  // ── Leaving: back to the page that started it (Connections by default) ───
  const leave = (ok: boolean, message = '', cancelled = false) => {
    if (marker.current) {
      finishOAuthPopup(marker.current, { ok, cancelled, message });
      setPopupDone(ok ? 'Google Analytics is connected.' : message || 'Google Analytics was not connected.');
      return;
    }
    leaveCallback(navigate, ok, { provider: 'google', brandId: brandRef.current }, queryClient);
  };

  const connected = (description: string) => {
    if (!marker.current) toast({ title: 'Google Analytics Connected ✅', description });
    leave(true, description);
  };

  const failAndExit = (message: string, cancelled = false) => {
    if (!marker.current) toast({ title: 'Connection Failed', description: message, variant: 'destructive' });
    leave(false, message, cancelled);
  };

  const isUuid = (v: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);

      const code = params.get('code');
      const rawState = params.get('state');
      const errorParam = params.get('error');

      const ru = buildRedirectUri('google');
      setRedirectUri(ru);
      // In a popup a page opened, the marker (localStorage, shared with that
      // page) names this run's state.
      marker.current = popupMarkerFor('google', rawState);

      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch {}

      // Whose sign-in this is, before any early return (a cancel included).
      const stateBrand = (() => {
        try {
          return decodeURIComponent(rawState ?? '').split(':')[0].replace(/^"+|"+$/g, '').trim();
        } catch {
          return '';
        }
      })();
      if (isUuid(stateBrand)) brandRef.current = stateBrand;

      if (errorParam) {
        // access_denied: the member declined, or closed the consent screen.
        return errorParam === 'access_denied'
          ? failAndExit('Google Analytics access was not granted.', true)
          : failAndExit('Google OAuth returned an error.');
      }

      if (!code || !rawState) {
        return failAndExit('Missing OAuth code or state.');
      }

      const decodedState = (() => {
        try {
          return decodeURIComponent(rawState);
        } catch {
          return rawState;
        }
      })();

      const extractedBrandId = decodedState.split(':')[0].replace(/^"+|"+$/g, '').trim();

      if (!isUuid(extractedBrandId)) {
        return failAndExit(`Invalid brandId in state: ${rawState}`);
      }

      setBrandId(extractedBrandId);
      brandRef.current = extractedBrandId;

      // Read once: a later connect must not be checked against this run's state.
      const expectedState = sessionStorage.getItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_state');
      // A popup's marker already matched the state. Its sessionStorage is a
      // copy of the page's, taken when it opened — before google-oauth-start
      // returned this run's state — so it can only hold an older run's.
      if (!marker.current && expectedState && expectedState !== rawState) {
        return failAndExit('OAuth state mismatch. Please try connecting again.');
      }

      try {
        setLoading(true);

        const { data, error } = await supabase.functions.invoke('google-oauth', {
          body: { code, brandId: extractedBrandId, redirectUri: ru },
        });

        if (error) throw error;

        if (data?.needsSelection) {
          setProperties(Array.isArray(data.properties) ? data.properties : []);
          setNeedsSelection(true);
          setLoading(false);
          return;
        }

        connected(data?.connectedProperty?.property_name
          ? `Connected to ${data.connectedProperty.property_name}`
          : 'Connected');
      } catch (err: any) {
        console.error('Google OAuth error:', err);
        failAndExit(await functionErrorMessage(err, 'Failed to complete Google connection'));
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const finalizeSelection = async () => {
    if (!brandId) return failAndExit('Missing brandId.');
    if (!selectedPropertyId) {
      toast({
        title: 'Select a property',
        description: 'Please choose a GA4 property to connect.',
      });
      return;
    }

    try {
      setSavingSelection(true);

      const { data, error } = await supabase.functions.invoke('google-oauth', {
        body: { brandId, redirectUri, selectedPropertyId },
      });

      if (error) throw error;

      setNeedsSelection(false);
      connected(data?.connectedProperty?.property_name
        ? `Connected to ${data.connectedProperty.property_name}`
        : 'Connected');
    } catch (err: any) {
      console.error('Finalize GA selection error:', err);
      const msg = await functionErrorMessage(err, 'Failed to save GA4 property selection');
      toast({ title: 'Connection Failed', description: msg, variant: 'destructive' });
    } finally {
      setSavingSelection(false);
    }
  };

  if (popupDone !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="space-y-2 text-center">
          <h2 className="text-xl font-semibold">{popupDone}</h2>
          <p className="text-muted-foreground">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4 w-full max-w-md px-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />

        <h2 className="text-2xl font-semibold">
          {needsSelection ? 'Choose a Google Analytics Property' : 'Connecting Google Analytics...'}
        </h2>

        <p className="text-muted-foreground">
          {needsSelection
            ? 'Select which GA4 property you want to connect to this brand.'
            : 'Please wait while we complete the connection.'}
        </p>

        {/* Selection Dialog */}
        <Dialog open={needsSelection} onOpenChange={(open) => !open && leave(false, 'No property was chosen.')}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Select GA4 Property</DialogTitle>
              <DialogDescription>
                Pick the property you want to use for analytics on this brand.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a property..." />
                </SelectTrigger>
                <SelectContent>
                  {grouped.map(([accountName, props]) => (
                    <div key={accountName} className="py-1">
                      <div className="px-2 py-1 text-xs text-muted-foreground">{accountName}</div>
                      {props.map((p) => (
                        <SelectItem key={p.property_id} value={p.property_id}>
                          {p.property_name || p.property_id}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => leave(false, 'No property was chosen.')}>
                  Cancel
                </Button>
                <Button onClick={finalizeSelection} disabled={savingSelection}>
                  {savingSelection ? 'Saving…' : 'Connect'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {!loading && !needsSelection && popupDone === null && (
          <Button variant="outline" onClick={() => leave(false)}>
            Back
          </Button>
        )}
      </div>
    </div>
  );
}